import type { VideosScope, VideosSort, VideosStatus, VideosVisibility } from '@/api/endpoints/videos'

/**
 * O search da tela Projects e os filtros dela. Uma função só remonta o
 * search, para nenhum ``navigate`` perder um parâmetro no caminho (eram três
 * montagens à mão). Defaults ficam fora da URL.
 */

export interface ProjectsSearch {
  channel?: string
  sort?: VideosSort
  page?: number
  status?: Exclude<VideosStatus, 'all'>
  scope?: Exclude<VideosScope, 'channel'>
  /** Na visão do pool, os slugs desmarcados, separados por vírgula. */
  hide?: string
  /** Entre os publicados, os baldes de visibilidade desmarcados, separados por vírgula. */
  vis?: string
}

/** Os slugs desmarcados no menu do pool, a partir do ``hide`` da URL. */
export function hiddenChannels(search: Pick<ProjectsSearch, 'hide'>): string[] {
  return (search.hide ?? '').split(',').filter(Boolean)
}

/** Os baldes do subfiltro de "Publicados", na ordem do menu. */
export const VISIBILITY_BUCKETS: readonly VideosVisibility[] = ['public', 'unlisted', 'private', 'scheduled']

export function isVisibility(v: unknown): v is VideosVisibility {
  return VISIBILITY_BUCKETS.includes(v as VideosVisibility)
}

/** Os baldes desmarcados, a partir do ``vis`` da URL; valor desconhecido sai. */
export function hiddenVisibility(search: Pick<ProjectsSearch, 'vis'>): VideosVisibility[] {
  return [...new Set((search.vis ?? '').split(',').filter(isVisibility))]
}

export function isScope(v: unknown): v is 'all' | 'pool' {
  return v === 'all' || v === 'pool'
}

export function isStatus(v: unknown): v is 'published' | 'unpublished' {
  return v === 'published' || v === 'unpublished'
}

/** O search novo a partir do anterior; ``page`` volta à 1 a menos que venha. */
export function nextProjectsSearch(
  prev: ProjectsSearch,
  patch: {
    sort?: VideosSort
    page?: number
    status?: VideosStatus
    scope?: VideosScope
    hide?: string[]
    vis?: VideosVisibility[]
  },
): ProjectsSearch {
  const sort = patch.sort ?? prev.sort ?? 'recent'
  const status = patch.status ?? prev.status ?? 'all'
  const scope = patch.scope ?? prev.scope ?? 'channel'
  const out: ProjectsSearch = {}
  if (prev.channel) out.channel = prev.channel
  if (sort !== 'recent') out.sort = sort
  if (status !== 'all') out.status = status
  if (scope !== 'channel') out.scope = scope
  // O menu de canais vale só para a visão do pool; sair dela o zera.
  const hide = patch.hide ?? hiddenChannels(prev)
  if (scope === 'pool' && hide.length > 0) out.hide = [...new Set(hide)].sort().join(',')
  // O de visibilidade, só entre os publicados; sair deles o zera.
  const vis = (patch.vis ?? hiddenVisibility(prev)).filter(isVisibility)
  if (status === 'published' && vis.length > 0) out.vis = [...new Set(vis)].sort().join(',')
  if (patch.page !== undefined && patch.page > 1) out.page = patch.page
  return out
}

/** Os rótulos do filtro de publicação: num canal, "publicados" é neste canal. */
export function statusOptions(scope: VideosScope): { value: VideosStatus; label: string }[] {
  return [
    { value: 'all', label: 'Todos' },
    { value: 'published', label: scope === 'channel' ? 'Publicados neste canal' : 'Publicados' },
    { value: 'unpublished', label: 'Não publicados' },
  ]
}

/** O selo de origem de um vídeo no card. */
export function originBadge(
  video: { channel: string; channel_name?: string; in_pool?: boolean },
  scope: VideosScope,
  activeChannel: string,
): { kind: 'pool' | 'channel'; label: string } | null {
  const name = video.channel_name || video.channel
  if (video.in_pool) return { kind: 'pool', label: `Pool ${name}` }
  // Num canal, o selo só aparece fora dele; nas outras visões, sempre: no
  // pool, é o canal que levou o vídeo.
  if (scope !== 'channel' || video.channel !== activeChannel) return { kind: 'channel', label: name }
  return null
}

export function isSort(v: unknown): v is VideosSort {
  return v === 'recent' || v === 'ranking' || v === 'views'
}

/**
 * A última escolha de filtros, lembrada neste navegador. Os links para a
 * tela (sidebar, logo, breadcrumb) levam a escolha; antes, ir a um vídeo e
 * voltar pelo menu zerava tudo. Canal e página ficam de fora:
 * o canal já vive no seletor, e a página não faz sentido fora da visita.
 */
export type RememberedFilters = Pick<ProjectsSearch, 'sort' | 'status' | 'scope'>

const FILTERS_KEY = 'prism.projects.filters'

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem'>

function browserStorage(): KeyValueStore | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** Grava os filtros de search (defaults ficam de fora, como na URL). */
export function rememberFilters(search: ProjectsSearch, storage = browserStorage()): void {
  const out: RememberedFilters = {}
  if (search.sort && search.sort !== 'recent') out.sort = search.sort
  if (search.status) out.status = search.status
  if (search.scope) out.scope = search.scope
  try {
    storage?.setItem(FILTERS_KEY, JSON.stringify(out))
  } catch {
    // Sem storage (aba privada, cota): a tela segue funcionando sem lembrar.
  }
}

/** Os filtros lembrados, validados; o que não for reconhecido é ignorado. */
export function rememberedFilters(storage = browserStorage()): RememberedFilters {
  let raw: unknown
  try {
    raw = JSON.parse(storage?.getItem(FILTERS_KEY) ?? 'null')
  } catch {
    return {}
  }
  if (!raw || typeof raw !== 'object') return {}
  const v = raw as Record<string, unknown>
  const out: RememberedFilters = {}
  if (isSort(v['sort']) && v['sort'] !== 'recent') out.sort = v['sort']
  if (isStatus(v['status'])) out.status = v['status']
  if (isScope(v['scope'])) out.scope = v['scope']
  return out
}

/**
 * O search de um link para Projects: o canal mais os filtros lembrados. Os
 * links de navegação (sidebar, logo, breadcrumb) levam a escolha na própria
 * URL, em vez de a rota restaurá-la ao chegar sem filtro: assim o voltar do
 * navegador devolve exatamente a URL de cada passo, inclusive a sem filtro.
 */
export function projectsLinkSearch(
  channel: string | null | undefined,
  remembered: RememberedFilters = rememberedFilters(),
): ProjectsSearch {
  return channel ? { channel, ...remembered } : { ...remembered }
}

/**
 * Restaura os filtros lembrados uma única vez por carregamento do app: na
 * primeira entrada em Projects sem filtro na URL (raiz digitada, favorito,
 * redirect do login). Depois disso, só os links levam a escolha: restaurar a
 * cada entrada sem filtro faria o voltar do navegador parecer não funcionar.
 */
export function createFirstVisitRestore(): (
  search: ProjectsSearch,
  remembered?: RememberedFilters,
) => ProjectsSearch | null {
  let done = false
  return (search, remembered = rememberedFilters()) => {
    if (done) return null
    done = true
    if (search.sort || search.status || search.scope) return null
    if (!remembered.sort && !remembered.status && !remembered.scope) return null
    return { ...search, ...remembered }
  }
}
